/**
 * Bilingual copy for future Apple StoreKit purchase UI states.
 *
 * Scaffolding only — not wired to a native purchase flow yet.
 */

export type AppleUiState =
  | "loading_products"
  | "product_unavailable"
  | "purchasing"
  | "pending"
  | "cancelled"
  | "purchase_success"
  | "verification_failed"
  | "restoring"
  | "restore_success"
  | "linked_to_another_account";

export type UiLang = "es" | "en";

export const APPLE_UI_COPY: Record<AppleUiState, Record<UiLang, string>> = {
  loading_products: {
    es: "Cargando planes de suscripción…",
    en: "Loading subscription plans…",
  },
  product_unavailable: {
    es: "Las opciones de suscripción no están disponibles temporalmente. Inténtalo de nuevo.",
    en: "Subscription options are temporarily unavailable. Please try again.",
  },
  purchasing: {
    es: "Procesando tu compra en la App Store…",
    en: "Processing your purchase in the App Store…",
  },
  pending: {
    es: "Tu compra está pendiente de aprobación (por ejemplo, permiso de un tutor).",
    en: "Your purchase is pending approval (for example, a guardian's consent).",
  },
  cancelled: {
    es: "Cancelaste la compra. No se realizó ningún cargo.",
    en: "You cancelled the purchase. No charge was made.",
  },
  purchase_success: {
    es: "¡Compra completada! Tu acceso PRO ya está activo.",
    en: "Purchase complete! Your PRO access is now active.",
  },
  verification_failed: {
    es: "No pudimos verificar tu compra con Apple. Intenta restaurar tus compras.",
    en: "We couldn't verify your purchase with Apple. Please try restoring your purchases.",
  },
  restoring: {
    es: "Restaurando tus compras anteriores…",
    en: "Restoring your previous purchases…",
  },
  restore_success: {
    es: "Compras restauradas correctamente.",
    en: "Purchases restored successfully.",
  },
  linked_to_another_account: {
    es: "Esta suscripción de Apple ya está vinculada a otra cuenta MBLearn.",
    en: "This Apple subscription is already linked to another MBLearn account.",
  },
};

export function getAppleUiCopy(state: AppleUiState, language: string): string {
  const lang: UiLang = language === "en" ? "en" : "es";
  return APPLE_UI_COPY[state][lang];
}

/**
 * Bilingual copy for the duplicate-subscription guard on iOS.
 *
 * Intentionally provider-neutral: no mention of the website, Stripe, external
 * stores or prices (Apple Guideline 3.1.3(b)).
 */
export const DUPLICATE_STRIPE_ON_APPLE_COPY: Record<UiLang, {
  title: string;
  alreadyActive: string;
  worksOnIos: string;
  managedByStripe: string;
  noAutoCancel: string;
}> = {
  es: {
    title: "Ya tienes una suscripción activa",
    alreadyActive: "Tu cuenta ya cuenta con una suscripción activa.",
    worksOnIos: "Tu acceso PRO funciona con normalidad en esta app.",
    managedByStripe: "No necesitas hacer nada más para seguir usando MBLearn PRO.",
    noAutoCancel: "No modificaremos tu suscripción actual automáticamente.",
  },
  en: {
    title: "You already have an active subscription",
    alreadyActive: "Your account already has an active subscription.",
    worksOnIos: "Your PRO access works normally in this app.",
    managedByStripe: "Nothing else is needed to keep using MBLearn PRO.",
    noAutoCancel: "We won't automatically change your current subscription.",
  },
};

/** Bilingual labels for the native iOS subscription surface. */
export const APPLE_STORE_COPY: Record<UiLang, {
  title: string;
  subtitle: string;
  monthly: string;
  quarterly: string;
  annual: string;
  perMonth: string;
  perQuarter: string;
  perYear: string;
  subscribe: string;
  restore: string;
  manage: string;
  restoreNothingFound: string;
  activeTitle: string;
  activeUntil: string;
  renewsAutomatically: string;
  cancelAnytime: string;
  retry: string;
  storeUnavailable: string;
  pluginUnavailable: string;
  legal: string;
}> = {
  es: {
    title: "MBLearn PRO",
    subtitle: "Elige tu plan y accede a todo el contenido.",
    monthly: "Mensual",
    quarterly: "Trimestral",
    annual: "Anual",
    perMonth: "por mes",
    perQuarter: "por trimestre",
    perYear: "por año",
    subscribe: "Suscribirme",
    restore: "Restaurar compras",
    manage: "Administrar suscripción",
    restoreNothingFound: "No encontramos compras anteriores en esta cuenta de App Store.",
    activeTitle: "Suscripción activa",
    activeUntil: "Activa hasta",
    renewsAutomatically:
      "La suscripción se renueva automáticamente salvo que se cancele al menos 24 h antes del final del período.",
    cancelAnytime: "Puedes cancelar cuando quieras desde Administrar suscripción.",
    retry: "Reintentar",
    storeUnavailable: "La tienda no está disponible en este momento.",
    pluginUnavailable: "El servicio de compras no se cargó en esta instalación de la app.",
    legal: "El pago se cargará a tu cuenta de App Store al confirmar la compra.",
  },
  en: {
    title: "MBLearn PRO",
    subtitle: "Choose your plan and unlock all content.",
    monthly: "Monthly",
    quarterly: "Quarterly",
    annual: "Annual",
    perMonth: "per month",
    perQuarter: "per quarter",
    perYear: "per year",
    subscribe: "Subscribe",
    restore: "Restore Purchases",
    manage: "Manage Subscription",
    restoreNothingFound: "No previous purchases were found for this App Store account.",
    activeTitle: "Active subscription",
    activeUntil: "Active until",
    renewsAutomatically:
      "Subscriptions renew automatically unless cancelled at least 24 hours before the end of the period.",
    cancelAnytime: "You can cancel anytime from Manage Subscription.",
    retry: "Retry",
    storeUnavailable: "The store is unavailable right now.",
    pluginUnavailable: "The purchase service was not loaded in this app installation.",
    legal: "Payment is charged to your App Store account at confirmation of purchase.",
  },
};
